/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Col,
    Input,
    InputGroup,
    InputGroupText,
    Label,
    Row
} from "reactstrap";
import { applyTriggerFilterFlags } from "../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { path_secondary_visual_check } from "../../../../actions/Tenants/config/constants";
import { find_category } from "../../../../actions/Tenants/config/utils";
import { usePatchCheckMutation, usePostCheckDefectMutation } from "../../../../hooks/mutations";
import { usePatchCheckStateLocally } from "../../../../hooks/mutations/usePatchCheckMutation";
import { useCheckImages } from "../../../../hooks/useCheckImages";
import { getActiveSessionId } from "../../../../offline/sessionManager";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import { getDefectGroupDenominator } from "../../../../utils/defectGroupDenominator";
import { validateRequiredImages } from "../../../../utils/imageValidation";
import MetaForm, { metaFormIsValid } from "../../../Forms/MetaForm";
import { TenantDefect, doFilterDefects, useTenantDefectDefinitions } from "../../../Forms/useManagedOptionsHook";
import { ColorCodedSpan } from "../../Helper/ColorCodedSpan";
import { toPercentage } from "../../Layers/fields/formatters";
import { useQcEntities } from "../QcEntityContext";
import Box from "./Layout";
import CheckImages from "./Summary/CheckImages";

import { useRouteCheckQuery } from "../../../../hooks/useCurrentEntities";

const appendend_style = css`width: 4.5rem;`;
const appended_error_style = css`width: 4.5rem;border-color: rgba(var(--bs-danger-rgb)) !important;color: rgba(var(--bs-danger-rgb))`;

export default function VisualCheck() {
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const config = useConfig();
    const isLoading = useRouteCheckQuery().isLoading;
    const { check, layer } = useQcEntities();
    const location_config = config.get_check_type_config(layer, check);

    // Determine if this is secondary visual check based on route
    const isSecondary = params.action === path_secondary_visual_check;

    // Dynamically select which form fields to use
    const visual_check_form = isSecondary
        ? location_config?.secondary_visual_check_form
        : location_config?.visual_check_form;
    const mappedVisualCheckForm = getFilledArrayOrDefault(visual_check_form).map((field: any) => (
        layer[field.default_value_from_layer_key] !== undefined
            ? { ...field, default_value: layer[field.default_value_from_layer_key] }
            : field
    ));

    const patchCheckStateLocally = usePatchCheckStateLocally();
    const patchCheckMutation = usePatchCheckMutation();
    const setCheckValue = (field, value, callApi = true) => {
        patchCheckStateLocally(check, { [field]: value });
        if (callApi) {
            patchCheckMutation.mutate({ check, field, value });
        }
    };

    // MetaForm only auto-triggers callbacks for fields with a non-empty default_value.
    // Fields that have a callback but no default_value (e.g. computed fields like sample_size)
    // need their callback triggered on mount so they can derive their value from other sources.
    useEffect(() => {
        if (!check?.check_id || !mappedVisualCheckForm.length) return;
        const fieldsNeedingInit = mappedVisualCheckForm.filter(
            (f: any) => f.onchange_callback && (!f.default_value || f.default_value === "")
                && (!check[f.name] || check[f.name] === "None")
        );
        fieldsNeedingInit.forEach((f: any) => {
            const callback = config.get_onchange_callback(f.onchange_callback);
            if (callback) {
                callback({
                    setValue: (field, value) => setCheckValue(field, value),
                    field: f.name,
                    value: check[f.name],
                    object: { ...layer, ...check },
                });
            }
        });
    }, [check?.check_id]);

    // Get first column width from defect form (DefectFrequencyForm will handle which one)
    const primary_defect_form = location_config?.visual_check_defect_form || [];
    const secondary_defect_form = location_config?.secondary_visual_check_defect_form || [];
    const active_defect_form = isSecondary ? secondary_defect_form : primary_defect_form;
    const first_column_width = getFilledArrayOrDefault(active_defect_form, [])[0]?.lg || 6;

    // Use the new config method to get filtered required images
    const required_images = config.get_check_required_images(layer, check);
    const currentFlow = location_config?.flow?.find((f) => f.path === params.action);
    const flowHasRequiredImages = !currentFlow?.disable_required_images;

    // Fetch check images for validation using TanStack Query
    const { data: images = [] } = useCheckImages(tenantId, check.check_id);

    const [defectsErrors, setDefectsErrors] = useState<Record<string, string | boolean>>({});
    const canSubmit = () => {
        const formValid = metaFormIsValid(mappedVisualCheckForm, check, false);
        const imagesValid = !flowHasRequiredImages || validateRequiredImages(required_images, images || [], false);
        const hasDefectErrors = isFilledArray(Object.values(defectsErrors));

        return formValid && !hasDefectErrors && imagesValid;
    };

    return (
        <Box hideButtons={isLoading} canSubmit={canSubmit()}>
            {!isLoading && (
                <>
                    <Row>
                        <Col lg={first_column_width}>
                            <MetaForm
                                meta={mappedVisualCheckForm}
                                object={check}
                                onDebounce={(field, value) => setCheckValue(field, value, true)}
                                setValue={(field, value) => setCheckValue(field, value, false)}
                                config={config}
                            />
                        </Col>
                    </Row>

                    <DefectFrequencyForm setDefectsErrors={setDefectsErrors} defectsErrors={defectsErrors}/>

                    {check.check_id > 0 && flowHasRequiredImages && (
                        <div>
                            <CheckImages tenant_id={tenantId} check_id={check.check_id} required_images={required_images} />
                        </div>
                    )}
                </>
            )}
        </Box>
    );
}


export const DefectFrequencyForm = ({ setDefectsErrors, defectsErrors = {} }: { setDefectsErrors: (errors: Record<string, string | boolean>) => void, defectsErrors?: Record<string, string | boolean> }) => {
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const config = useConfig();
    const { check, layer } = useQcEntities();
    const location_config = config.get_check_type_config(layer, check);
    const patchCheckStateLocally = usePatchCheckStateLocally();
    const patchCheckMutation = usePatchCheckMutation();
    const postDefectMutation = usePostCheckDefectMutation();
    const frequency = check.defects as { [key: string]: number };

    const tenantDefectDefinitions = useTenantDefectDefinitions(layer.fruit_type) as TenantDefect[];

    // Determine if this is secondary visual check based on route and select appropriate defect form
    const isSecondary = params.action === path_secondary_visual_check;
    const visual_check_defect_form = isSecondary
        ? location_config?.secondary_visual_check_defect_form
        : location_config?.visual_check_defect_form;

    const updateCheckDefectsState = (defect_id, frequency) => {
        const defects = { ...check.defects, [defect_id]: Number(frequency) };
        const visual_defects = (Object.values(defects) as number[]).reduce((acc, freq) => acc + freq, 0);
        const patch: any = { defects, visual_defects };
        // Offline there is no server round-trip to populate `defect_list`
        // (what the pre-submit summary reads — see DefectGroup), so mirror the
        // optimistic `defects` map into it. Online we leave `defect_list`
        // alone: the next getCheck refreshes it from the server with the
        // richer per-registration shape.
        if (getActiveSessionId()) {
            patch.defect_list = Object.entries(defects).map(([id, freq]) => ({ defect_id: id, frequency: Number(freq) }));
        }
        patchCheckStateLocally(check, patch);
    };

    const callUpdateCheckDefects = (defect_id, frequency) => {
        postDefectMutation.mutate({ tenantId, checkId: check.check_id, defectId: defect_id, frequency: Number(frequency) || 0 });
    };

    let input_groups = useMemo(() => {
        return getFilledArrayOrDefault(visual_check_defect_form, []).map((i) => ({
            ...i,
            defects: doFilterDefects({ defect_position: i.defect_position, defect_severity: i.defect_severity }, tenantDefectDefinitions)
        })).filter((i) => i.defects.length > 0);
    }, [tenantDefectDefinitions, visual_check_defect_form]);

    input_groups = input_groups.map((g) => {
        // * Fall back to the layer's sample_size when the check hasn't received/persisted
        // * its own yet, mirroring the same fallback used server-side in flagged_metric.py.
        const denominator = getDefectGroupDenominator(g, check.sample_size || layer.sample_size, check.avocados.length);
        return {
            ...g,
            denominator,
            show_group_total: 1,
            group_total: g.defects.reduce((total, i) => total + (Number(frequency[i.defect_id]) || 0), 0),

            form: g.defects.map((i) => ({
                label: i.label,
                value: frequency[i.defect_id] || 0,
                appendend_style: frequency[i.defect_id] > denominator ? appended_error_style : appendend_style,
                name: i.defect_id,
                image_url: i.image_url,
                type: "number",
                min: 0,
                max: denominator,
                unity: toPercentage(frequency[i.defect_id], denominator, g.decimals || 1),
            }))
        };
    }).map((g) => ({
        ...g,
        exceeds_max: g.group_total > g.denominator,
        below_min: g.warn_below_100 === 1 && g.group_total < g.denominator,
        has_error: g.exceeds_max || g.below_min,
    }));

    const first_column_width = getFilledArrayOrDefault(visual_check_defect_form, [])[0]?.lg || 6;
    const decimals_for_total = Math.min(...input_groups.map((i) => i.decimals || 1));
    const total_defects = input_groups.filter((i) => !i.exclude_from_total).reduce((total: number, i) => total + i.group_total, 0);

    useEffect(() => {
        const errors: Record<string, string | boolean> = {};

        // First check if totals exceed sample_size
        if (total_defects > Number(check.sample_size)) {
            errors.total_exceeds_max = `Total defects must be lower than ${check.sample_size}`;
        }

        // Then check if any groups are supposed to be exactly 100% (== denominator) or exceed their denominator
        input_groups.forEach((group, index) => {
            const { denominator } = group;
            if (group.warn_below_100 && group.group_total < Number(denominator)) {
                errors[`group_${index}_below_min`] = `Group defects should be minimum ${denominator}`;
            }
            // * only add error if total_exceeds_max is not already set
            if (!errors.total_exceeds_max && group.group_total > Number(denominator)) {
                errors[`group_${index}_exceeds_max`] = `Group defects must be lower than ${denominator}`;
            }
        });

        setDefectsErrors(errors);
    }, [total_defects, check.sample_size, input_groups.map((i) => i.group_total).join(","), input_groups.map((i) => i.warn_below_100).join(","), input_groups.map((i) => i.denominator).join(",")]);

    useEffect(() => {


        input_groups.forEach((group) => {

            if (group.onchange_callback) {
                const callback = config.onchange_callbacks[group.onchange_callback];

                if (typeof callback === "function") {
                    const setCheckValue = (field: string, value: any) => {
                        console.log("Setting check value", field, value); // eslint-disable-line no-console
                        patchCheckMutation.mutate({ check, field, value });
                        patchCheckStateLocally(check, { [field]: value });
                    };

                    console.log("Defect frequency @ onchange_callback", frequency); // eslint-disable-line no-console

                    callback({
                        check,
                        group,
                        frequency,
                        setCheckValue,
                    });
                }
            }
        });
    }, [input_groups.map((i) => i.group_total).join(","), Object.values(frequency).join(",")]);

    return <>
        <Row className="pb-3">
            {input_groups.map((group, index) => <Col key={index} className="pt-3" lg={group.lg}>
                <MetaForm
                    config={config}
                    meta={group.title ? [{ type: "header", label: group.title, options: group.defects }, ...group.form] : group.form}
                    object={frequency}
                    setValue={updateCheckDefectsState}
                    onDebounce={callUpdateCheckDefects}
                />
                {group.show_group_total > 0 && <div className="mb-2">
                    <Label>Total</Label>
                    <InputGroup >
                        <Input readOnly disabled type="number" className={group.has_error ? "text-start is-invalid" : "text-start" } value={group.group_total || ""} />
                        <InputGroupText css={group.has_error ? appended_error_style : appendend_style}>{toPercentage(group.group_total, group.denominator, group.decimals || 1)}</InputGroupText>
                    </InputGroup>
                    <div className="position-relative">
                        {group.exceeds_max && <small className="position-absolute text-danger">Total must be lower than {group.denominator}</small>}
                        {defectsErrors[`group_${index}_below_min`] && typeof defectsErrors[`group_${index}_below_min`] === "string" && <small className="position-absolute text-danger">{defectsErrors[`group_${index}_below_min`]}</small>}
                    </div>
                </div>}
            </Col>)}
        </Row>
        {input_groups.length > 1
            && <Row className="pb-3 mb-3">
                <Col lg={first_column_width}>
                    <Label>Overall total</Label>
                    <div className="d-flex align-items-center">
                        <InputGroup >
                            <Input readOnly disabled type="number" className={"exceeds_max" in defectsErrors && defectsErrors.exceeds_max ? "text-start is-invalid" : "text-start" } value={total_defects || ""} />
                            <InputGroupText css={"exceeds_max" in defectsErrors && defectsErrors.exceeds_max ? appended_error_style : appendend_style}>{toPercentage(total_defects, input_groups[0]?.denominator ?? check.sample_size, decimals_for_total)}</InputGroupText>
                        </InputGroup>
                    </div>
                    <div className="position-relative">
                        {"exceeds_max" in defectsErrors && defectsErrors.exceeds_max && typeof defectsErrors.exceeds_max === "string" && <small className="position-absolute text-danger">{defectsErrors.exceeds_max}</small>}
                    </div>
                </Col>
            </Row>
        }
    </>;

};

export const DefectGroupSummary = () => {
    const config = useConfig();
    const { check, layer } = useQcEntities();
    const location_config = config.get_check_type_config(layer, check);
    const tenantDefectDefinitions = useTenantDefectDefinitions(layer.fruit_type);

    // * defect_group is the array to be used for reporting (can also include internal defect which you dont use in visual check)
    const { defect_groups } = location_config || {};
    const defect_group_map = defect_groups?.map((defect_group, index) => <DefectGroup key={index} defect_group={defect_group} tenantDefectDefinitions={tenantDefectDefinitions} />) || [];

    const visualCheckForms = [
        ...getFilledArrayOrDefault(location_config?.visual_check_form),
        ...getFilledArrayOrDefault(location_config?.secondary_visual_check_form),
    ];
    const formFields = visualCheckForms
        .filter((field, index) => visualCheckForms.findIndex(({ name }) => name === field.name) === index)
        .map((field) => ({ label: field.label, value: check[field.name] }));

    return [
        formFields.length > 0 && <div className="pe-5 align-self-start" key={0}>
            <table>
                <tbody>
                    {formFields.map((i, index) => <tr key={index}>
                        <th >{i.label}: </th>
                        <td className="ps-3 text-capitalize">{i.value}</td>
                    </tr>)}
                </tbody>
            </table>
        </div>,
        ...defect_group_map.map((i, index) => <div className="pe-5 align-self-start" key={index + 1}>
            {i}
        </div>).filter((i) => i)
    ];

};


// This is the html version of PDFCheckChildrenDefects
function DefectGroup({ defect_group, tenantDefectDefinitions }) {
    const { check, layer } = useQcEntities();
    const group_defects = doFilterDefects({ defect_position: defect_group.defect_position, defect_severity: defect_group.defect_severity }, tenantDefectDefinitions);
    const group_defect_ids = group_defects.map((i) => i.defect_id);
    const metric_meta_key = `defects_${[...(defect_group.defect_position || []), ...(defect_group.defect_severity || [])].join("_")}`;
    // * set this flag using the color coding wizard
    const total_flag_name = `flag_${metric_meta_key}`;
    // * Fall back to the layer's sample_size (e.g. computed upstream by the ingest
    // * service) when the check hasn't received/persisted its own yet, mirroring the
    // * same fallback used server-side in flagged_metric.py's get_denominator().
    const denominator = getDefectGroupDenominator(defect_group, check.sample_size || layer.sample_size, check.avocados.length);
    const flags = useMemo(() => applyTriggerFilterFlags(defect_group.flags || [], layer, check), [layer, check]);

    // * Use this function to get the flag color for indivudal defects
    const getFlag = useCallback((value) => {
        const flag = find_category(value / denominator, flags);
        if (flag) {
            return flag.flag;
        }
        return "";
    }, [flags, denominator]);


    // Defects registered on fruit by fruit
    const fruit_defects = check.avocados.flatMap((avocado) => avocado.defects);

    // We assume here that what ever is registered on a check level via visual check or ART is not also registered on a fruit level
    const registered_defects = [...check.defect_list, ...fruit_defects];

    const total_defects_per_type = registered_defects.reduce((acc, i) => ({ ...acc, [i.defect_id]: (acc[i.defect_id] || 0) + i.frequency }), {});
    // * Sum from the same rows rendered below, so the header total always matches their sum
    const sum_of_all_defects = group_defect_ids.reduce((acc, defect_id) => acc + (total_defects_per_type[defect_id] || 0), 0);

    const decimals = defect_group.decimals || 1;
    const showGroupTotal = defect_group.show_group_total !== 0 && defect_group.show_group_total !== "0";
    const headerLabel = `${defect_group.title || ""}${showGroupTotal ? ":" : ""}`;

    return <table>
        <tbody>
            <tr>
                <th css={css`max-width: 20rem;`} >
                    {headerLabel}
                </th>
                <td className="ps-3">
                    {showGroupTotal ? <ColorCodedSpan flag={check[total_flag_name]}> {toPercentage(sum_of_all_defects, denominator, decimals)}</ColorCodedSpan> : null}
                </td>
            </tr>
            {group_defects.filter((i) => {
                const numerator = total_defects_per_type[i.defect_id] || 0; // Use 0 if undefined


                // Calculate percentage
                if (denominator && denominator > 0) {
                    const percentage = (numerator / denominator) * 100;
                    // Round the percentage to the specified decimals
                    const roundedPercentage = Math.round(percentage * 10 ** decimals) / 10 ** decimals;

                    return roundedPercentage > 0; // Filter out 0% values
                }

                return false; // Exclude if denominator is not valid
            }).map((i, index) => <tr key={index}>
                <th className="text-truncate" css={css`max-width: 20rem;`} >{i.label}:</th>
                <td className="ps-3">
                    <ColorCodedSpan flag={getFlag(total_defects_per_type[i.defect_id])}>{toPercentage(total_defects_per_type[i.defect_id], denominator, decimals)}</ColorCodedSpan>
                </td>
            </tr>)}

        </tbody>
    </table>;
}
